import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Factory,
  Package,
  ArrowLeft,
  ArrowDown,
  Drop,
  CheckCircle,
  Warning,
  ArrowsClockwise,
  ClipboardText,
  ListChecks,
  Printer,
  Play,
} from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

export default function Lavorazione() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null); // { phaseKey, phase, expectedQty }
  const [actualQty, setActualQty] = useState("");
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/${orderId}/workflow`);
      setData(data);
    } catch (err) {
      if (err?.response?.status !== 401) toast.error(formatApiError(err));
      navigate("/ordini");
    } finally {
      setLoading(false);
    }
  }, [orderId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const printCommessa = async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/commessa.pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      // Revoke after a small delay so the new tab can load it
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const phaseKey = (itemIdx, phaseIdx) => `item-${itemIdx}-phase-${phaseIdx}`;

  const startPhase = async (key) => {
    try {
      await api.post(`/orders/${orderId}/phase-progress`, {
        phase_key: key,
        status: "started",
      });
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openComplete = (key, phase, expectedQty) => {
    setCompleting({ phaseKey: key, phase, expectedQty });
    setActualQty(String(expectedQty));
    setReason("");
  };

  const submitComplete = async () => {
    const actual = Number(actualQty);
    if (Number.isNaN(actual) || actual < 0) {
      toast.error("Quantità non valida");
      return;
    }
    if (Math.abs(actual - completing.expectedQty) > 0.001 && !reason.trim()) {
      toast.error("Inserisci la motivazione: la quantità è diversa dal previsto");
      return;
    }
    try {
      await api.post(`/orders/${orderId}/phase-progress`, {
        phase_key: completing.phaseKey,
        status: "completed",
        actual_qty: actual,
        expected_qty: completing.expectedQty,
        reason: reason.trim() || null,
      });
      toast.success("Fase completata");
      setCompleting(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const phaseStatus = (key) => data?.progress?.[key]?.status;

  if (loading) {
    return (
      <div className="p-12 text-center text-sm text-[#4B5563]">Caricamento workflow…</div>
    );
  }

  if (!data) return null;

  return (
    <div className="p-8 lg:p-12 fade-up" data-testid="lavorazione-page">
      <button
        onClick={() => navigate("/ordini")}
        className="text-xs uppercase tracking-wider text-[#4B5563] hover:text-[#0A0A0A] flex items-center gap-2 mb-4"
      >
        <ArrowLeft size={14} weight="bold" /> Torna agli ordini
      </button>

      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-2">
            Workflow Operatore · Lavorazione Ordine
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A]">
            {data.order_number}
          </h1>
          <div className="text-sm text-[#4B5563] mt-2">
            Cliente: <b className="text-[#0A0A0A]">{data.customer_name}</b>
            {data.delivery_date && (
              <>
                {" · "}Consegna:{" "}
                <span className="font-mono-data">
                  {new Date(data.delivery_date).toLocaleDateString("it-IT")}
                </span>
              </>
            )}
            {" · "}Stato:{" "}
            <span className="uppercase tracking-wider text-[10px] bg-[#F9FAFB] border border-[#E5E7EB] px-2 py-1">
              {data.status}
            </span>
          </div>
        </div>
        <button
          onClick={printCommessa}
          data-testid="print-commessa-btn"
          className="bg-[#1D3557] hover:bg-[#0A0A0A] text-white px-5 py-3 text-xs font-display font-semibold uppercase tracking-wider flex items-center gap-2"
        >
          <Printer size={14} weight="bold" /> Stampa Commessa
        </button>
      </div>

      {data.items.map((item, itemIdx) => (
        <div key={item.product_id} className="mb-12" data-testid={`item-${itemIdx}`}>
          {/* Item header */}
          <div className="bg-[#1D3557] text-white p-6 mb-4">
            <div className="text-[10px] uppercase tracking-wider text-white/70 mb-1">
              Prodotto da realizzare
            </div>
            <div className="flex items-baseline justify-between gap-4 flex-wrap">
              <div>
                <div className="font-display text-2xl font-bold">{item.product_name}</div>
                {item.formula_name && (
                  <div className="text-xs text-white/70 mt-1">
                    Formula: {item.formula_name}
                  </div>
                )}
              </div>
              <div className="font-mono-data text-3xl font-bold">
                {item.quantity_ordered} pz
              </div>
            </div>
          </div>

          {/* SL breakdown summary */}
          {item.sl_breakdown.length > 0 && (
            <div className="bg-white border border-[#E5E7EB] p-5 mb-6">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] mb-3">
                Riepilogo semilavorati
              </div>
              {item.sl_breakdown.map((sl) => (
                <div
                  key={sl.sl_id}
                  className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm py-2 border-t border-[#E5E7EB] first:border-t-0 first:pt-0"
                >
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">
                      Semilavorato
                    </div>
                    <div className="font-medium text-[#0A0A0A]">{sl.sl_name}</div>
                    <div className="text-[10px] text-[#4B5563]">
                      {sl.sl_per_unit_L} L per pezzo
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">
                      Totale necessario
                    </div>
                    <div className="font-mono-data font-bold text-[#0A0A0A]">
                      {sl.total_needed_L} L
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">
                      Stock disponibile
                    </div>
                    <div
                      className={`font-mono-data ${
                        sl.stock_available_L > 0 ? "text-[#2A9D8F]" : "text-[#4B5563]"
                      }`}
                    >
                      {sl.stock_available_L} L
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">
                      Da produrre
                    </div>
                    <div className="font-mono-data text-[#1D3557] font-bold">
                      {sl.to_produce_L} L
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">
                      Batch necessari
                    </div>
                    <div className="font-mono-data font-bold text-[#0A0A0A]">
                      {sl.batches_needed} × {sl.batch_size_L} L
                    </div>
                    <div className="text-[10px] text-[#4B5563]">
                      max {sl.max_units_per_batch} pz/batch
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Phases */}
          {item.phases.length === 0 ? (
            <div className="bg-[#FBEDEE] border border-[#E63946]/30 p-6 text-sm text-[#E63946]">
              ⚠ Nessuna distinta base disponibile per questo prodotto. Crea la formula in
              "Formule & Distinte" per generare il workflow.
            </div>
          ) : (
            <div className="space-y-1" data-testid="phases-list">
              {item.phases.map((phase, idx) => {
                const key = phaseKey(itemIdx, idx);
                const status = phaseStatus(key);
                const progress = data?.progress?.[key];
                return (
                  <PhaseCard
                    key={idx}
                    phase={phase}
                    index={idx}
                    total={item.phases.length}
                    status={status}
                    progress={progress}
                    onStart={() => startPhase(key)}
                    onComplete={(expectedQty) => openComplete(key, phase, expectedQty)}
                    orderId={orderId}
                    phaseKeyId={key}
                    onAfterResidue={load}
                  />
                );
              })}
            </div>
          )}
        </div>
      ))}

      <div className="mt-8 bg-[#FFF8E7] border border-[#F4A261]/40 p-5 text-xs text-[#4B5563]">
        <div className="flex items-start gap-3">
          <ListChecks size={20} weight="duotone" color="#F4A261" />
          <div>
            <div className="font-display font-semibold text-[#0A0A0A] mb-1 uppercase tracking-wider text-[11px]">
              Come usare questa pagina
            </div>
            <p>
              1. <b>Stampa la commessa</b> (PDF) per portare il foglio in laboratorio con le ricette
              dettagliate. 2. Quando inizi una fase, premi <b>Avvia</b>. 3. A fine fase premi{" "}
              <b>Completa</b> e inserisci la quantità effettiva: se differisce dal previsto, dovrai
              indicarne la motivazione.
            </p>
          </div>
        </div>
      </div>

      {/* Complete phase dialog */}
      <Dialog
        open={!!completing}
        onOpenChange={(v) => {
          if (!v) setCompleting(null);
        }}
      >
        <DialogContent className="max-w-md" data-testid="complete-phase-dialog">
          <DialogHeader>
            <DialogTitle>Conferma quantità</DialogTitle>
          </DialogHeader>
          {completing && (
            <div className="space-y-4">
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-3 text-sm">
                <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">Fase</div>
                <div className="font-display font-semibold">
                  {completing.phase.type === "prepare_batch"
                    ? `Prepara batch ${completing.phase.batch_n}/${completing.phase.total_batches} di ${completing.phase.sl_name}`
                    : `Imbottiglia ${completing.phase.units_to_fill} pz di ${completing.phase.pf_name}`}
                </div>
                <div className="text-xs text-[#4B5563] mt-1">
                  Atteso: <b className="font-mono-data">{completing.expectedQty}</b>{" "}
                  {completing.phase.type === "prepare_batch" ? "L" : "pz"}
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                  Quantità effettiva *
                </label>
                <input
                  type="number"
                  step="0.01"
                  data-testid="actual-qty-input"
                  autoFocus
                  className="w-full border border-[#E5E7EB] px-3 py-3 text-lg font-mono-data outline-none focus:ring-2 focus:ring-[#2A9D8F]"
                  value={actualQty}
                  onChange={(e) => setActualQty(e.target.value)}
                />
              </div>

              {Math.abs(Number(actualQty) - completing.expectedQty) > 0.001 && (
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-[#E63946] block mb-1">
                    Motivazione (obbligatoria, quantità diversa) *
                  </label>
                  <textarea
                    data-testid="reason-input"
                    rows={3}
                    className="w-full border border-[#F4A261] bg-[#FFF8E7] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F4A261]"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Es: perdita schiuma, errore di taratura, residuo in vasca, calo viscosità…"
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <button
              type="button"
              onClick={() => setCompleting(null)}
              className="px-4 py-2 border border-[#E5E7EB] text-xs uppercase tracking-wider text-[#4B5563]"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={submitComplete}
              data-testid="submit-complete-btn"
              className="px-4 py-2 bg-[#2A9D8F] hover:bg-[#21867A] text-white text-xs font-semibold uppercase tracking-wider flex items-center gap-1"
            >
              <CheckCircle size={12} weight="fill" />
              Conferma
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PhaseCard({ phase, index, total, status, progress, onStart, onComplete, orderId, phaseKeyId, onAfterResidue }) {
  const colorByType = {
    prepare_batch: { bg: "#1D3557", icon: Factory, label: "Prepara Batch" },
    bottle: { bg: "#2A9D8F", icon: Package, label: "Imbottiglia & Confeziona" },
    use_stock: { bg: "#457B9D", icon: ArrowsClockwise, label: "Usa Stock Esistente" },
    handle_residue: { bg: "#F4A261", icon: Drop, label: "Gestione Residuo" },
  };
  const c = colorByType[phase.type] || colorByType.prepare_batch;
  const Icon = c.icon;
  const isCompleted = status === "completed";
  const isStarted = status === "started";
  const expectedQty =
    phase.type === "prepare_batch"
      ? phase.expected_output_L
      : phase.type === "bottle"
      ? phase.units_to_fill
      : 0;
  const showActions = phase.type === "prepare_batch" || phase.type === "bottle";

  return (
    <div data-testid={`phase-${index}`}>
      {index > 0 && (
        <div className="flex justify-center py-1">
          <ArrowDown size={20} weight="bold" color="#4B5563" />
        </div>
      )}
      <div
        className={`bg-white border-2 ${
          isCompleted
            ? "border-[#2A9D8F] opacity-80"
            : isStarted
            ? "border-[#F4A261] shadow-lg"
            : "border-[#E5E7EB]"
        } transition-colors`}
      >
        <div
          className="px-5 py-3 flex items-center justify-between"
          style={{
            background: isCompleted ? "#2A9D8F" : c.bg,
            color: "white",
          }}
        >
          <div className="flex items-center gap-3">
            <span
              className="inline-flex items-center justify-center w-8 h-8 bg-white text-sm font-mono-data font-bold"
              style={{ color: isCompleted ? "#2A9D8F" : c.bg }}
            >
              {isCompleted ? <CheckCircle size={18} weight="fill" /> : index + 1}
            </span>
            <Icon size={20} weight="duotone" />
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/80">
                Fase {index + 1} di {total}
                {isCompleted && " · COMPLETATA"}
                {isStarted && " · IN CORSO"}
              </div>
              <div className="font-display font-bold">{c.label}</div>
            </div>
          </div>
          {showActions && (
            <div className="flex items-center gap-2">
              {!isStarted && !isCompleted && (
                <button
                  onClick={onStart}
                  data-testid={`start-phase-${index}`}
                  className="bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1"
                >
                  <Play size={12} weight="fill" /> Avvia
                </button>
              )}
              {isStarted && !isCompleted && (
                <button
                  onClick={() => onComplete(expectedQty)}
                  data-testid={`complete-phase-${index}`}
                  className="bg-white text-[#1D3557] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1 hover:bg-[#F9FAFB]"
                >
                  <CheckCircle size={12} weight="fill" /> Completa
                </button>
              )}
            </div>
          )}
        </div>

        <div className="p-5">
          {phase.type === "prepare_batch" && <PreparePhase phase={phase} />}
          {phase.type === "bottle" && <BottlePhase phase={phase} />}
          {phase.type === "use_stock" && <UseStockPhase phase={phase} />}
          {phase.type === "handle_residue" && (
            <ResiduePhase
              phase={phase}
              orderId={orderId}
              phaseKey={phaseKeyId}
              status={status}
              progress={progress}
              onDone={onAfterResidue}
            />
          )}

          {progress && progress.actual_qty != null && (
            <div className="mt-3 bg-[#F9FAFB] border-l-4 border-[#2A9D8F] p-3 text-xs">
              <div className="font-mono-data text-[#0A0A0A]">
                ✓ Effettivo: <b>{progress.actual_qty}</b>
                {progress.expected_qty != null && (
                  <span className="text-[#4B5563]">
                    {" "}
                    (atteso: {progress.expected_qty})
                  </span>
                )}
              </div>
              {progress.reason && (
                <div className="text-[#4B5563] italic mt-1">
                  Motivo: {progress.reason}
                </div>
              )}
              {progress.updated_by && (
                <div className="text-[#4B5563] text-[10px] mt-1">
                  da {progress.updated_by} ·{" "}
                  {new Date(progress.updated_at).toLocaleString("it-IT")}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PreparePhase({ phase }) {
  return (
    <div>
      <div className="grid grid-cols-3 gap-4 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">
            Batch numero
          </div>
          <div className="font-mono-data text-2xl font-bold text-[#0A0A0A]">
            {phase.batch_n} <span className="text-base text-[#4B5563]">/ {phase.total_batches}</span>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">
            Output atteso
          </div>
          <div className="font-mono-data text-2xl font-bold text-[#1D3557]">
            {phase.expected_output_L} L
          </div>
          <div className="text-xs text-[#4B5563]">{phase.sl_name}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">
            Formula
          </div>
          <div className="text-sm font-medium text-[#0A0A0A]">{phase.formula_name}</div>
        </div>
      </div>
      {phase.preparation_instructions && (
        <div className="bg-[#F9FAFB] border-l-4 border-[#1D3557] p-3 mt-2">
          <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-semibold mb-1 flex items-center gap-1">
            <ClipboardText size={12} weight="bold" /> Istruzioni di preparazione
          </div>
          <div className="text-sm text-[#0A0A0A] whitespace-pre-wrap">
            {phase.preparation_instructions}
          </div>
        </div>
      )}
    </div>
  );
}

function BottlePhase({ phase }) {
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">
            Pezzi da riempire
          </div>
          <div className="font-mono-data text-3xl font-bold text-[#2A9D8F]">
            {phase.units_to_fill}
          </div>
          <div className="text-xs text-[#4B5563]">{phase.pf_name}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">
            Volume per pezzo
          </div>
          <div className="font-mono-data text-xl font-bold text-[#0A0A0A]">
            {phase.sl_per_unit_L} L
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">
            Volume consumato
          </div>
          <div className="font-mono-data text-xl font-bold text-[#0A0A0A]">
            {phase.L_consumed} L
          </div>
          <div className="text-xs text-[#4B5563]">dal batch {phase.batch_n}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">
            Residuo in vasca
          </div>
          <div
            className={`font-mono-data text-xl font-bold ${
              phase.residual_L_in_tank > 50 ? "text-[#F4A261]" : "text-[#4B5563]"
            }`}
          >
            {phase.residual_L_in_tank} L
          </div>
          {phase.is_last_batch && phase.residual_L_in_tank > 50 && (
            <div className="text-[10px] text-[#F4A261] font-semibold uppercase tracking-wider mt-1">
              ⚠ da gestire
            </div>
          )}
        </div>
      </div>
      <div className="text-xs text-[#4B5563] bg-[#F9FAFB] p-2">
        Imbottiglia <b>{phase.units_to_fill} pz</b> di <b>{phase.pf_name}</b> usando{" "}
        <b>{phase.L_consumed} L</b> dal batch in vasca di <b>{phase.sl_name}</b>.
      </div>
    </div>
  );
}

function UseStockPhase({ phase }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">
          Pezzi da riempire
        </div>
        <div className="font-mono-data text-2xl font-bold text-[#457B9D]">
          {phase.units_to_fill}
        </div>
        <div className="text-xs text-[#4B5563]">{phase.pf_name}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">
          SL da stock
        </div>
        <div className="font-mono-data text-2xl font-bold text-[#0A0A0A]">
          {phase.L_consumed} L
        </div>
        <div className="text-xs text-[#4B5563]">{phase.sl_name}</div>
      </div>
      <div className="flex items-center">
        <CheckCircle size={20} weight="duotone" color="#2A9D8F" />
        <span className="ml-2 text-xs text-[#4B5563]">
          Già disponibile in magazzino, niente da produrre.
        </span>
      </div>
    </div>
  );
}

function ResiduePhase({ phase, orderId, phaseKey, status, progress, onDone }) {
  const [allSL, setAllSL] = useState([]);
  const [targetSL, setTargetSL] = useState(phase.sl_id);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isCompleted = status === "completed";

  useEffect(() => {
    api.get("/products", { params: { type: "semilavorato" } }).then(({ data }) => {
      setAllSL(data);
      if (!targetSL && data.length > 0) {
        // Default to the same SL of the tank
        setTargetSL(phase.sl_id || data[0].id);
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!targetSL) {
      toast.error("Seleziona un semilavorato");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/orders/${orderId}/residue-to-stock`, {
        phase_key: phaseKey,
        residue_L: phase.residual_L,
        target_sl_id: targetSL,
        notes,
      });
      toast.success(
        `${data.residue_L_added} L di ${data.target_sl_name} caricati a stock (totale: ${data.new_stock} L)`
      );
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <div className="font-mono-data text-3xl font-bold text-[#F4A261]">
          {phase.residual_L} L
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">
            Residui in vasca di
          </div>
          <div className="text-sm font-bold text-[#0A0A0A]">{phase.sl_name}</div>
        </div>
      </div>

      {phase.alternative_orders.length > 0 && !isCompleted && (
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] mb-2">
            🎯 Ordini pendenti che usano questo semilavorato
          </div>
          <div className="space-y-2">
            {phase.alternative_orders.map((alt, i) => (
              <div
                key={i}
                className="bg-[#FFF8E7] border border-[#F4A261]/40 p-3 flex items-center justify-between"
              >
                <div>
                  <div className="text-xs font-mono-data text-[#0A0A0A]">
                    {alt.order_number} · {alt.customer_name}
                  </div>
                  <div className="text-sm font-medium">{alt.pf_name}</div>
                  <div className="text-[10px] text-[#4B5563]">
                    {alt.qty_ordered} pz richiesti · {alt.sl_per_unit_L} L per pezzo
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono-data text-sm font-bold text-[#F4A261]">
                    {Math.floor(phase.residual_L / alt.sl_per_unit_L)} pz fattibili
                  </div>
                  <div className="text-[10px] text-[#4B5563]">
                    con {phase.residual_L} L residui
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 text-[11px] text-[#4B5563] italic">
            💡 Cambia configurazione macchina e riempi i pezzi sopra, oppure metti il residuo
            a stock nel semilavorato di tua scelta:
          </div>
        </div>
      )}

      {!isCompleted ? (
        <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#1D3557] mb-3">
            Come esauriremo la vasca? Quale semilavorato finirà in stock?
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#4B5563] block mb-1">
                Semilavorato di destinazione
              </label>
              <select
                data-testid="residue-target-select"
                value={targetSL || ""}
                onChange={(e) => setTargetSL(e.target.value)}
                className="w-full border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#1D3557]"
              >
                {allSL.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} · stock attuale: {p.stock} {p.unit}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#4B5563] block mb-1">
                Note operatore (opz.)
              </label>
              <input
                data-testid="residue-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Es. diluizione X, motivo..."
                className="w-full border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#1D3557]"
              />
            </div>
          </div>
          <button
            onClick={submit}
            disabled={submitting || !targetSL}
            data-testid="residue-submit-btn"
            className="bg-[#1D3557] hover:bg-[#0A0A0A] disabled:opacity-50 text-white px-5 py-2.5 text-xs font-display font-semibold uppercase tracking-wider flex items-center gap-2"
          >
            <CheckCircle size={14} weight="fill" />
            {submitting ? "Salvataggio…" : `Metti ${phase.residual_L} L a stock`}
          </button>
        </div>
      ) : (
        <div className="bg-[#E6F4F1] border border-[#2A9D8F]/40 p-4">
          <CheckCircle size={20} weight="fill" color="#2A9D8F" className="inline mr-2" />
          <span className="text-sm text-[#0A0A0A]">
            <b>{progress?.actual_qty} L</b> caricati come{" "}
            <b>{progress?.target_sl_name}</b> a stock.
          </span>
          {progress?.notes && (
            <div className="text-xs text-[#4B5563] italic mt-1">Note: {progress.notes}</div>
          )}
        </div>
      )}
    </div>
  );
}
